import React from 'react'
import { Product, StockBatch, CartItem } from '@/types/pos'
import { Package, Calendar, Hash, X, ShoppingCart } from 'lucide-react'

interface BatchSelectionModalProps {
  product: Product
  batches: StockBatch[]
  cart: CartItem[]
  onSelect: (batch: StockBatch) => void
  onCancel: () => void
}

export const BatchSelectionModal: React.FC<BatchSelectionModalProps> = ({
  product,
  batches,
  cart,
  onSelect,
  onCancel,
}) => {
  // Sort batches: oldest first (FIFO)
  const sortedBatches = [...batches].sort(
    (a, b) => new Date(a.receivedDate || a.createdAt).getTime() - new Date(b.receivedDate || b.createdAt).getTime()
  )

  // Helper: get qty already in cart for a given batch
  const getCartQtyForBatch = (batchId: string) => {
    const pid = product.id || product._id
    const item = cart.find(ci =>
      (ci.product.id || ci.product._id) === pid && ci.batchId === batchId
    )
    return item ? item.quantity : 0
  }

  const formatDate = (dateStr: string | Date | null | undefined) => {
    if (!dateStr) return '—'
    const d = new Date(dateStr)
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
  }

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-md flex items-center justify-center z-50 p-4">
      <div className="glass-card max-w-lg w-full border-primary/30">
        {/* Header */}
        <div className="flex justify-between items-start mb-6">
          <div>
            <h3 className="text-xl font-bold esales-text">Select Stock Batch</h3>
            <p className="text-sm text-muted-foreground mt-1">
              Multiple batches found for <span className="font-semibold text-foreground">{product.name}</span>
            </p>
            <p className="text-xs text-muted-foreground mt-0.5">
              Different prices detected — please select which batch the customer is purchasing from.
            </p>
          </div>
          <button
            onClick={onCancel}
            className="p-2 rounded-lg hover:bg-muted transition-colors flex-shrink-0"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Batch Cards */}
        <div className="space-y-3 max-h-[50vh] overflow-y-auto pr-1 custom-scrollbar">
          {sortedBatches.map((batch, index) => {
            const isOldest = index === 0
            const inCart = getCartQtyForBatch(batch.id)
            const remaining = (batch.currentStock || 0) - inCart
            const isSoldOut = remaining <= 0

            return (
              <button
                key={batch.id}
                onClick={() => !isSoldOut && onSelect(batch)}
                disabled={isSoldOut}
                className={`w-full text-left p-4 rounded-xl border-2 transition-all group relative ${
                  isSoldOut
                    ? 'border-transparent bg-muted/10 opacity-50 cursor-not-allowed'
                    : 'border-transparent bg-muted/30 hover:bg-primary/10 hover:border-primary/40'
                }`}
              >
                {/* Age indicator */}
                {isOldest && (
                  <span className="absolute top-3 right-3 px-2 py-0.5 bg-amber-500/20 text-amber-400 text-[10px] font-bold rounded uppercase tracking-wider">
                    Old Stock
                  </span>
                )}
                {!isOldest && index === sortedBatches.length - 1 && (
                  <span className="absolute top-3 right-3 px-2 py-0.5 bg-green-500/20 text-green-400 text-[10px] font-bold rounded uppercase tracking-wider">
                    New Stock
                  </span>
                )}

                {/* Price - prominently displayed */}
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center transition-colors ${
                      isSoldOut ? 'bg-muted/20' : 'bg-primary/10 group-hover:bg-primary/20'
                    }`}>
                      <Package className={`w-5 h-5 ${isSoldOut ? 'text-muted-foreground' : 'text-primary'}`} />
                    </div>
                    <div>
                      <p className={`text-2xl font-black ${isSoldOut ? 'text-muted-foreground' : 'text-primary'}`}>
                        Rs.{batch.sellingPrice.toFixed(2)}
                      </p>
                      {batch.costPrice > 0 && (
                        <p className="text-[10px] text-muted-foreground">
                          Cost: Rs.{batch.costPrice.toFixed(2)}
                        </p>
                      )}
                    </div>
                  </div>
                  <div className="text-right">
                    {isSoldOut ? (
                      <p className="text-sm font-bold text-red-400">All in cart</p>
                    ) : (
                      <>
                        <p className="text-lg font-bold text-foreground">{remaining}</p>
                        <p className="text-[10px] text-muted-foreground uppercase tracking-wider">Available</p>
                      </>
                    )}
                    {inCart > 0 && (
                      <div className="flex items-center gap-1 justify-end mt-0.5">
                        <ShoppingCart className="w-3 h-3 text-blue-400" />
                        <span className="text-[10px] font-bold text-blue-400">{inCart} in cart</span>
                      </div>
                    )}
                  </div>
                </div>

                {/* Details row */}
                <div className="flex items-center gap-4 text-xs text-muted-foreground">
                  <div className="flex items-center gap-1">
                    <Hash className="w-3 h-3" />
                    <span className="font-mono">{batch.batchNumber.length > 20 ? batch.batchNumber.slice(0, 20) + '...' : batch.batchNumber}</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <Calendar className="w-3 h-3" />
                    <span>Received: {formatDate(batch.receivedDate)}</span>
                  </div>
                  {batch.expiryDate && (
                    <div className="flex items-center gap-1">
                      <Calendar className="w-3 h-3 text-amber-400" />
                      <span className="text-amber-400">Expires: {formatDate(batch.expiryDate)}</span>
                    </div>
                  )}
                </div>

                {/* Supplier info */}
                {batch.supplierName && (
                  <p className="text-[10px] text-muted-foreground mt-2">
                    Supplier: {batch.supplierName}
                  </p>
                )}
              </button>
            )
          })}
        </div>

        {/* Footer */}
        <div className="mt-6 pt-4 border-t border-border">
          <button
            onClick={onCancel}
            className="w-full py-3 bg-muted hover:bg-muted/80 rounded-xl font-bold text-xs uppercase tracking-widest transition-colors"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  )
}

export default BatchSelectionModal
